import { z } from 'zod';

// ───────────────────────────── Zamówienia do dostawców, dostawy, faktury (docs/14 §2.9–2.10) ─────────────────────────────

export const PO_STATUSES = {
  robocze: 'Robocze',
  zlozone: 'Złożone',
  czesciowo_dostarczone: 'Częściowo dostarczone',
  dostarczone: 'Dostarczone',
  zamkniete: 'Zamknięte z brakiem',
  anulowane: 'Anulowane',
} as const;
export type PoStatus = keyof typeof PO_STATUSES;

export const PLACED_METHODS = { email: 'E-mailem', telefon: 'Telefonicznie', portal: 'Przez portal dostawcy', osobiscie: 'Osobiście', inny: 'Inaczej' } as const;
export type PlacedMethod = keyof typeof PLACED_METHODS;

const str = (max: number) => z.string().trim().max(max);
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data w formacie RRRR-MM-DD');

export const supplierInputSchema = z.object({
  name: str(200).min(2, 'Podaj nazwę dostawcy'),
  nip: z
    .string()
    .trim()
    .transform((v) => v.replace(/[\s-]/g, ''))
    .refine((v) => v === '' || /^\d{10}$/.test(v), 'NIP: 10 cyfr')
    .default(''),
  email: str(200).default(''),
  phone: str(30).default(''),
  leadTimeDays: z.number().int().min(0).max(365).nullable().default(null),
  note: str(1000).default(''),
  active: z.boolean().default(true),
});
export type SupplierInput = z.infer<typeof supplierInputSchema>;

export interface SupplierDto {
  id: string;
  name: string;
  nip: string;
  email: string;
  phone: string;
  leadTimeDays: number | null;
  note: string;
  active: boolean;
}

export const poLineInputSchema = z.object({
  id: z.string().uuid().nullable().default(null),
  itemId: z.string().uuid(),
  packId: z.string().uuid().nullable().default(null),
  packQty: z.number().positive().max(1_000_000).nullable().default(null),
  /** w jednostce magazynowej (z opakowań liczone na serwerze) */
  quantity: z.number().positive().max(10_000_000),
  /** cena za jednostkę magazynową w groszach waluty zamówienia */
  unitPriceGr: z.number().min(0).max(100_000_000_00).nullable().default(null),
  expectedDate: day.nullable().default(null),
  /** pokrycie potrzeb: pozycja zapotrzebowania → ilość; reszta pozycji to zapas */
  allocations: z.array(z.object({ demandLineId: z.string().uuid(), quantity: z.number().positive() })).max(100).default([]),
});
export type PoLineInput = z.infer<typeof poLineInputSchema>;

export const poInputSchema = z.object({
  companyId: z.string().uuid(),
  supplierId: z.string().uuid(),
  warehouseId: z.string().uuid(),
  currency: z.string().regex(/^[A-Z]{3}$/).default('PLN'),
  expectedDate: day.nullable().default(null),
  note: str(2000).default(''),
  lines: z.array(poLineInputSchema).min(1, 'Dodaj co najmniej jedną pozycję').max(300),
});
export type PoInput = z.infer<typeof poInputSchema>;

export const poPlaceSchema = z.object({
  method: z.enum(['email', 'telefon', 'portal', 'osobiscie', 'inny']),
  note: str(500).default(''),
  /** potwierdzony przez dostawcę termin (opcjonalnie) */
  expectedDate: day.nullable().default(null),
});

export const poCloseSchema = z.object({ reason: str(500).min(3, 'Podaj przyczynę') });

/** Przyjęcie dostawy do zamówienia: ilości faktycznie otrzymane (w opakowaniach albo jednostce magazynowej). */
export const poReceiveSchema = z.object({
  docDate: day,
  externalNumber: str(80).default(''),
  /** kurs waluty zamówienia (PLN — zawsze 1; inna waluta — wymagany) */
  exchangeRate: z.number().positive().max(100_000).nullable().default(null),
  note: str(1000).default(''),
  lines: z
    .array(z.object({ orderLineId: z.string().uuid(), quantity: z.number().min(0).max(10_000_000), unitPriceGr: z.number().min(0).nullable().default(null) }))
    .min(1)
    .max(300),
  requestKey: str(80).min(8).nullable().default(null),
});
export type PoReceiveInput = z.infer<typeof poReceiveSchema>;

export const poQuerySchema = z.object({
  status: z.string().max(30).optional(),
  supplierId: z.string().uuid().optional(),
  companyId: z.string().uuid().optional(),
  open: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});

export interface PoLineDto {
  id: string;
  position: number;
  itemId: string;
  code: string;
  name: string;
  unit: string;
  packId: string | null;
  packName: string | null;
  packQty: number | null;
  quantity: number;
  received: number;
  cancelled: number;
  remaining: number;
  /** tylko z uprawnieniem do cen zakupu */
  unitPriceGr: number | null;
  expectedDate: string | null;
  allocations: { id: string; demandLineId: string | null; demandId: string | null; caseNumber: string | null; taskNumber: string | null; neededBy: string | null; quantity: number; received: number }[];
}

export interface PoDto {
  id: string;
  number: string;
  status: PoStatus;
  companyId: string;
  companyCode: string;
  supplierId: string;
  supplierName: string;
  warehouseId: string;
  warehouseCode: string;
  currency: string;
  expectedDate: string | null;
  /** po terminie, a nie dostarczone */
  late: boolean;
  placedAt: string | null;
  placedByName: string | null;
  placedMethod: PlacedMethod | null;
  placedNote: string;
  note: string;
  closedReason: string;
  createdByName: string | null;
  createdAt: string;
  /** tylko z uprawnieniem do cen zakupu */
  valueGr: number | null;
  lines: PoLineDto[];
  receipts: { documentId: string; number: string | null; docDate: string; externalNumber: string }[];
}

// ───────── faktura KSeF → przyjęcie ─────────

export type MatchConfidence = 'pewne' | 'propozycja' | 'brak';

/** Propozycja przyjęcia z faktury: dostawca, zamówienia, dopasowanie pozycji (niepewne — do potwierdzenia). */
export interface InvoiceReceiptProposalDto {
  invoiceId: string;
  invoiceNumber: string;
  companyId: string;
  currency: string;
  /** kurs z faktury (KursWaluty pozycji); PLN — 1; null — faktura w walucie bez kursu */
  exchangeRate: number | null;
  /** faktura korygująca — nie przyjmuje się z niej towaru */
  corrective: boolean;
  supplier: { id: string | null; name: string; nip: string; matched: boolean };
  openOrders: { id: string; number: string; status: PoStatus; expectedDate: string | null }[];
  lines: {
    lp: number;
    name: string;
    unit: string;
    quantity: number | null;
    unitNetGr: number | null;
    index: string;
    gtin: string;
    /** już powiązano z przyjęciami */
    linkedQuantity: number;
    match: { itemId: string; name: string; unit: string; packId: string | null; packName: string | null; factor: number; by: string } | null;
    confidence: MatchConfidence;
    /** pozycja otwartego zamówienia tego dostawcy z tym produktem */
    orderLineId: string | null;
  }[];
  /** przyjęcia (PZ) tego dostawcy / zamówień do powiązania bez ponownego przyjęcia */
  receipts: { documentId: string; number: string | null; docDate: string; lines: { lineId: string; itemId: string; name: string; quantity: number; linked: number }[] }[];
}

/** Utworzenie przyjęcia z faktury (ilości potwierdzone przez użytkownika) — PZ i powiązanie pozycji faktury. */
export const invoiceReceiveSchema = z.object({
  warehouseId: z.string().uuid(),
  supplierId: z.string().uuid().nullable().default(null),
  docDate: day,
  /** kurs waluty faktury; null — z faktury (KursWaluty); PLN — zawsze 1 */
  exchangeRate: z.number().positive().max(100_000).nullable().default(null),
  lines: z
    .array(
      z.object({
        lp: z.number().int().min(1),
        itemId: z.string().uuid(),
        packId: z.string().uuid().nullable().default(null),
        /** ilość faktycznie otrzymana — w opakowaniach, gdy packId, inaczej w jednostce magazynowej */
        quantity: z.number().positive().max(10_000_000),
        /** cena netto za jednostkę magazynową (gr waluty faktury) — z faktury przeliczona na opakowanie */
        unitPriceGr: z.number().min(0),
        orderLineId: z.string().uuid().nullable().default(null),
        /** zapamiętaj oznaczenie dostawcy dla tego produktu */
        remember: z.boolean().default(true),
      }),
    )
    .min(1)
    .max(300),
  requestKey: str(80).min(8).nullable().default(null),
});
export type InvoiceReceiveInput = z.infer<typeof invoiceReceiveSchema>;

/** Powiązanie faktury z istniejącym przyjęciem (towar przyszedł przed fakturą) — bez ruchu magazynowego. */
export const invoiceLinkSchema = z.object({
  links: z.array(z.object({ lp: z.number().int().min(1), receiptLineId: z.string().uuid(), quantity: z.number().positive() })).min(1).max(300),
});

export interface InvoiceWarehouseDto {
  /** ceny w PLN za jednostkę magazynową; corrected — różnica już skorygowana dokumentem KOR */
  links: { id: string; lp: number; receiptLineId: string; documentId: string; documentNumber: string | null; itemName: string; quantity: number; receiptUnitPriceGr: number | null; invoiceUnitPriceGr: number | null; differenceGr: number | null; corrected: boolean }[];
  /** pozycje faktury bez powiązania (np. usługi, transport — niemagazynowe) */
  unlinkedLps: number[];
  /** faktura korygująca: numer faktury pierwotnej i jej przyjęcia (towaru nie przyjmuje się z korekty) */
  corrective: { correctedNumber: string | null; receipts: { documentId: string; number: string | null }[] } | null;
}

/** Korekta wartości przyjęcia do ceny z faktury (decyzja 8): zmienia średnią od teraz, bez zmiany ilości i wcześniejszych wydań. */
export const valueCorrectionSchema = z.object({ linkId: z.string().uuid(), reason: str(500).min(3, 'Podaj przyczynę') });

// ───────── podpowiedzi zakupowe i pulpit magazyniera (docs/14 §2.11, §3) ─────────

/** Podpowiedź zakupu dla produktu w magazynie: braki potwierdzonych realizacji + uzupełnienie do minimum − dostępne − w zamówieniach. */
export interface PurchaseSuggestionDto {
  warehouseId: string;
  warehouseCode: string;
  companyId: string;
  itemId: string;
  code: string;
  name: string;
  unit: string;
  available: number;
  /** w otwartych zamówieniach, nieprzypisane do potrzeb (zapas w drodze) */
  incomingFree: number;
  minQty: number | null;
  /** na potwierdzone realizacje (braki niepokryte zamówieniami i wolnym towarem) */
  forDemands: number;
  /** do uzupełnienia minimum */
  forMin: number;
  quantity: number;
  earliestNeededBy: string | null;
  /** „zamów do” = najwcześniejszy termin potrzeby − czas dostawy */
  orderBy: string | null;
  leadTimeDays: number | null;
  supplierId: string | null;
  supplierName: string | null;
  /** średnie wydania z 90 dni (informacyjnie) */
  avgMonthlyIssue: number;
  lines: { demandId: string; lineId: string; caseNumber: string | null; taskNumber: string | null; neededBy: string | null; shortage: number }[];
  explanation: string;
}

export interface WarehouseDashboardDto {
  /** zgłoszenia ekip nowe / zmienione do przejrzenia */
  unreviewed: { id: string; caseNumber: string | null; taskNumber: string | null; changed: boolean; at: string }[];
  /** pozycje opisowe do dopasowania */
  unmatched: number;
  packagesToday: { id: string; number: string; status: string; caseNumber: string | null; taskNumber: string | null; teamName: string | null }[];
  packagesTomorrow: { id: string; number: string; status: string; caseNumber: string | null; taskNumber: string | null; teamName: string | null }[];
  /** braki zagrażające realizacjom (termin w ciągu 3 dni roboczych) */
  atRisk: { demandId: string; caseNumber: string | null; taskNumber: string | null; neededBy: string | null; readiness: string }[];
  lateDeliveries: { id: string; number: string; supplierName: string; expectedDate: string | null }[];
  deliveriesToday: { id: string; number: string; supplierName: string; expectedDate: string | null }[];
  /** faktury KSeF znanych dostawców bez przyjęcia (30 dni) — tylko z podglądem zakupów */
  invoicesToReceive: { id: string; invoiceNumber: string; sellerName: string; issueDate: string | null }[] | null;
  suggestions: number;
  belowMin: number;
}
